import { useState } from "react";
import { Copy, Check, Key } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { Modal } from "../../components/Modal/Modal";
import { CreateProjectResponse } from "./api";
import { truncateId } from "./projectRow";
import styles from "./RevealApiKeyModal.module.css";

interface Props {
  result: CreateProjectResponse | null;
  /** Called from the secondary CTA — modal closes; user stays on the list. */
  onStayOnList?: () => void;
  /** Called from the primary CTA — closes and navigates to the new project. */
  onOpenProject?: (projectId: string) => void;
  /**
   * Back-compat: legacy single-action close. Used when neither
   * onStayOnList nor onOpenProject are wired. Maps to "I saved it".
   */
  onClose?: () => void;
}

export function RevealApiKeyModal({
  result,
  onStayOnList,
  onOpenProject,
  onClose,
}: Props) {
  const [copied, setCopied] = useState(false);
  if (!result) return null;

  const { project, api_key_plaintext } = result;
  const title = project.name
    ? `${project.name} created`
    : "Project created";

  async function copy() {
    try {
      await navigator.clipboard.writeText(api_key_plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — clipboard may be unavailable in non-secure contexts.
    }
  }

  function handleStay() {
    if (onStayOnList) onStayOnList();
    else onClose?.();
  }

  function handleOpen() {
    if (onOpenProject) onOpenProject(project.id);
    else onClose?.();
  }

  return (
    <Modal open onClose={handleStay} title={title} footer={
      <>
        <Button variant="secondary" onClick={handleStay}>
          Stay on list
        </Button>
        <Button onClick={handleOpen}>
          Open project →
        </Button>
      </>
    }>
      <div className={styles.headerIcon} aria-hidden="true">
        <Key size={18} />
      </div>

      <Alert variant="success" title="Project created">
        Project ID <code className={styles.idCode}>{truncateId(project.id)}</code>
      </Alert>

      <p className={styles.lead}>
        Here's the project's API key. Copy it if you need it now — it's also
        always available from the project's API Keys page.
      </p>

      <div className={styles.keyBox}>
        <code className={styles.key}>{api_key_plaintext}</code>
        <button
          type="button"
          className={styles.copyBtn}
          onClick={() => void copy()}
        >
          {copied ? (
            <Check size={12} aria-hidden="true" />
          ) : (
            <Copy size={12} aria-hidden="true" />
          )}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>

    </Modal>
  );
}
